import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import type { CandidateWaitingPopup } from "@shared/candidateWaiting";
import {
  browserStorage,
  claimWaitingAlert,
  closeWaitingSystemNotification,
  isDesktopPointer,
  newTabId,
  openWaitingPageNotificationKeys,
  ownsWaitingAlert,
  readDismissedWaiting,
  readNotifyOffered,
  rememberDismissedWaiting,
  rememberNotifyOffered,
  shouldOfferDesktopNotifications,
  showWaitingSystemNotification,
  WAITING_DISMISSED_KEY,
  WAITING_NOTIFY_OFFERED_KEY,
  waitingAlertDelayMs,
  waitingEnterTarget,
  waitingForThisPage,
  waitingOpenMode,
} from "@/lib/candidateWaitingAlerts";
import { usePageVisible } from "@/hooks/usePageVisible";

/**
 * Пауза перед звонком: две вкладки могли застолбить одно ожидание почти
 * одновременно, звонит та, чья запись в хранилище осталась последней.
 */
const CLAIM_SETTLE_MS = 300;

/** Человек хоть раз нажимал что-то в этой вкладке — значит, звук ей разрешён. */
function tabHasBeenActive(): boolean {
  // Старый Safari не знает userActivation — считаем вкладку обычной.
  return navigator.userActivation?.hasBeenActive ?? true;
}

function notificationPermission(): NotificationPermission | null {
  return typeof Notification === "undefined" ? null : Notification.permission;
}

/**
 * Поведение карточек «кандидат ждёт вас в комнате» во вкладке: что показать,
 * когда позвонить, куда вести кнопку «Войти в комнату».
 *
 *  • Закрыл в одной вкладке — закрыто во всех (localStorage + событие storage).
 *  • Звук и системное уведомление — один раз на период ожидания, одной
 *    вкладкой из всех открытых. Вкладка на экране звонит сразу, скрытая —
 *    только если за 20 секунд этого не сделала видимая. Карточка без звука
 *    (рекрутёр на другой встрече) не звонит; перестала быть «тихой» —
 *    позвонит тогда.
 *  • Системное уведомление — только из скрытой вкладки и если разрешение уже
 *    дано; снимается, как только карточка исчезла, и сразу по «Войти» или
 *    «Скрыть».
 *  • Из живой встречи «Войти в комнату» открывает новую вкладку.
 *  • В комнате интервью X карточки «кандидат X ждёт» нет: там своя кнопка
 *    входа, а копия той же комнаты выбила бы разговор в первой.
 */
export function useCandidateWaitingAlerts(
  waiting: readonly CandidateWaitingPopup[],
  chime: () => void
) {
  const [location, navigate] = useLocation();
  const storage = useMemo(() => browserStorage(), []);
  const tabId = useMemo(() => newTabId(), []);
  const [dismissed, setDismissed] = useState<Set<string>>(() =>
    storage ? readDismissedWaiting(storage) : new Set()
  );
  const [permission, setPermission] = useState(notificationPermission);
  const [offered, setOffered] = useState(() => readNotifyOffered(storage));
  const pageVisible = usePageVisible();
  /** Периоды, по которым эта вкладка уже решила, звонить ли. */
  const handledRef = useRef<Set<string>>(new Set());
  /** Периоды, которые эта вкладка видела на экране: их уведомления снимаем, когда уйдут. */
  const shownRef = useRef<Set<string>>(new Set());
  const timersRef = useRef<number[]>([]);
  /** Отложенные звонки скрытой вкладки: период → таймер. */
  const pendingRef = useRef<Map<string, number>>(new Map());
  /** Последний список карточек — для отложенного звонка, который срабатывает позже рендера. */
  const latestRef = useRef<readonly CandidateWaitingPopup[]>([]);

  // Соседняя вкладка закрыла карточку или уже спросила про уведомления.
  useEffect(() => {
    if (!storage) return;
    const onStorage = (event: StorageEvent) => {
      if (event.key === WAITING_DISMISSED_KEY)
        setDismissed(readDismissedWaiting(storage));
      if (event.key === WAITING_NOTIFY_OFFERED_KEY)
        setOffered(readNotifyOffered(storage));
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [storage]);

  // Разрешение могли дать или отозвать в настройках браузера, пока вкладка жила.
  useEffect(() => {
    setPermission(notificationPermission());
  }, [pageVisible]);

  useEffect(
    () => () => {
      timersRef.current.forEach(timer => window.clearTimeout(timer));
      pendingRef.current.forEach(timer => window.clearTimeout(timer));
    },
    []
  );

  const visible = useMemo(
    () =>
      waitingForThisPage(waiting, location).filter(
        item => !dismissed.has(item.key)
      ),
    [waiting, dismissed, location]
  );

  const dismiss = useCallback(
    (item: CandidateWaitingPopup) => {
      setDismissed(
        storage
          ? rememberDismissedWaiting(storage, item.key)
          : current => new Set(current).add(item.key)
      );
      // Сразу, а не проходом ниже: переход в комнату пересоздаёт карточки
      // раньше, чем этот проход успеет увидеть, что карточки больше нет.
      void closeWaitingSystemNotification(item.key);
    },
    [storage]
  );

  const enter = useCallback(
    (item: CandidateWaitingPopup) => {
      // Рекрутёр пошёл в комнату — карточка больше не нужна ни в одной вкладке,
      // не дожидаясь, пока сервер увидит его там на следующем опросе.
      dismiss(item);
      const target = waitingEnterTarget(window.location.pathname, item.link);
      // Эта комната уже открыта здесь — вторую копию не открываем.
      if (target === "here") return;
      if (target === "new-tab") {
        window.open(item.link, "_blank", "noopener");
      } else {
        navigate(item.link);
      }
    },
    [dismiss, navigate]
  );

  /** Застолбить звонок за этой вкладкой и, если достался ей, позвонить. */
  const alertOnce = useCallback(
    (item: CandidateWaitingPopup) => {
      if (storage && !claimWaitingAlert(storage, item.key, tabId)) return;
      const timer = window.setTimeout(() => {
        timersRef.current = timersRef.current.filter(id => id !== timer);
        if (storage && !ownsWaitingAlert(storage, item.key, tabId)) return;
        chime();
        if (document.visibilityState === "hidden") {
          void showWaitingSystemNotification(item, () => enter(item));
        }
      }, CLAIM_SETTLE_MS);
      timersRef.current.push(timer);
    },
    [storage, tabId, chime, enter]
  );

  // Системные уведомления, показанные прежним экземпляром, — тоже наши. При
  // переходе в живую встречу и обратно DashboardLayout меняет обёртку, карточки
  // пересоздаются, и память о показанном пропадает; без этого уведомление,
  // которое «висит, пока не нажмут», осталось бы висеть. Проход ниже закроет
  // те, чьих карточек здесь нет. Он объявлен позже и потому идёт после.
  useEffect(() => {
    for (const key of openWaitingPageNotificationKeys())
      shownRef.current.add(key);
  }, []);

  // Звук и системное уведомление — один раз на период, одной вкладкой.
  useEffect(() => {
    latestRef.current = visible;
    const current = new Set(visible.map(item => item.key));
    for (const item of visible) {
      shownRef.current.add(item.key);
      if (item.silent || handledRef.current.has(item.key)) continue;
      const delay = waitingAlertDelayMs({
        pageVisible,
        hasBeenActive: tabHasBeenActive(),
      });
      if (delay === 0) {
        const pending = pendingRef.current.get(item.key);
        if (pending !== undefined) window.clearTimeout(pending);
        pendingRef.current.delete(item.key);
        handledRef.current.add(item.key);
        alertOnce(item);
        continue;
      }
      if (pendingRef.current.has(item.key)) continue;
      const timer = window.setTimeout(() => {
        pendingRef.current.delete(item.key);
        // Вкладку за это время открыли — решит ветка «на экране».
        if (document.visibilityState !== "hidden") return;
        // Карточку закрыли, рекрутёр вошёл или сейчас на другой встрече.
        const latest = latestRef.current.find(entry => entry.key === item.key);
        if (!latest || latest.silent || handledRef.current.has(item.key))
          return;
        handledRef.current.add(item.key);
        alertOnce(latest);
      }, delay);
      pendingRef.current.set(item.key, timer);
    }
    for (const [key, timer] of [...pendingRef.current]) {
      if (current.has(key)) continue;
      window.clearTimeout(timer);
      pendingRef.current.delete(key);
    }
    // Карточка ушла (рекрутёр вошёл, кандидат ушёл, закрыли) — уведомление тоже.
    for (const key of [...shownRef.current]) {
      if (current.has(key)) continue;
      shownRef.current.delete(key);
      void closeWaitingSystemNotification(key);
    }
  }, [visible, pageVisible, alertOnce]);

  const offerNotifications = shouldOfferDesktopNotifications({
    supported: permission !== null,
    permission,
    alreadyOffered: offered,
    desktop: isDesktopPointer(),
  });

  const enableNotifications = useCallback(() => {
    rememberNotifyOffered(storage);
    setOffered(true);
    if (typeof Notification === "undefined") return;
    // Спрашиваем только по нажатию: браузеры глушат сайты, которые просят сами.
    void Notification.requestPermission()
      .then(result => setPermission(result))
      .catch((error: unknown) => {
        console.warn(
          "[CandidateWaiting] разрешение на уведомления не запрошено:",
          error
        );
        setPermission(notificationPermission());
      });
  }, [storage]);

  return {
    visible,
    enter,
    dismiss,
    /** Сейчас открыта живая встреча: комната откроется в новой вкладке. */
    opensNewTab: waitingOpenMode(location) === "new-tab",
    offerNotifications,
    enableNotifications,
  };
}
